import React, { useState, useRef, memo, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  a11yDark,
  atomDark,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import { FaCheck } from "react-icons/fa";
import { LuCopy } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { TfiReload } from "react-icons/tfi";
import { AiOutlineExpandAlt } from "react-icons/ai";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiDislike } from "react-icons/bi";
import classes from "./ResponseMessage.module.css"; // Your CSS module
import { dracula } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Text } from "components/common";
import { HiOutlineDownload } from "react-icons/hi";
import { copyToClipboard, detectLanguage } from "utils/utils";
import { preprocessContent } from "utils/preprocessContent";
import clsx from "clsx";
import { BiError } from "react-icons/bi";
import { TbHandStop } from "react-icons/tb";
import { PiFan } from "react-icons/pi";
import ManageAccountLayout from "Layout/ManageAccountLayout/ManageAccountLayout";
import { deleteMessageById } from "db";

const ResponseMessage = memo(({ msg, userData, loading }) => {
  const [copied, setCopied] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const navigate = useNavigate();

  const handleUpgradeClick = useCallback(() => {
    if (!userData) {
      navigate("/login");
      return;
    }
    setShowSubscriptionModal(true);
  }, [userData, navigate]);

  const handleCopy = useCallback((code) => {
    copyToClipboard(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  }, []);

  const handleDownload = useCallback((code, language) => {
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `code.${language}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // Custom renderer for code blocks
  const renderCodeBlock = ({ node, inline, className, children, ...props }) => {
    const codeContent = String(children).replace(/\n$/, "");

    // Extract language from markdown code block (e.g., ```python)
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : detectLanguage(codeContent); // Fallback to detection

    return !inline ? (
      <CodeBlock
        code={codeContent}
        language={language}
        handleCopy={handleCopy}
        handleDownload={handleDownload}
      />
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  };

  return (
    <div className={clsx(classes.wrapper)}>
      {/* Error/Cancel Message */}
      {msg.error && (
        <div
          className={clsx(
            classes.errorMessage,
            msg.content === "User cancelled request" && classes.cancelMessage,
            msg.content.includes("Upgrade to PRO") && classes.upgradeMessage
          )}
        >
          <div className={classes.errorIcon}>
            {msg.content === "User cancelled request" ? (
              <TbHandStop size={18} />
            ) : msg.content.includes("Upgrade to PRO") ? (
              <PiFan size={18} />
            ) : (
              <BiError size={18} />
            )}
          </div>
          <div className={classes.errorContent}>
            <div className={classes.markdownContainer}>
              {msg.content}
              {msg.content.includes("Upgrade to PRO") && (
                <button
                  className={classes.upgradeButton}
                  onClick={handleUpgradeClick}
                >
                  Upgrade Now
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Regular Message Content */}
      {!msg.error && (
        <>
          <div className={classes.markdownContainer}>
            <ReactMarkdown
              children={preprocessContent(msg.content)}
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{ code: renderCodeBlock }}
              skipHtml={false}
            />
          </div>

          {/* Action Buttons */}

          <div className={classes.actionContainer}>
            {[
              // { icon: <TfiReload />, onClick: () => {} },
              // { icon: <AiOutlineExpandAlt />, onClick: () => {} },
              {
                icon: <RiDeleteBin6Line />,
                onClick: () => {
                  deleteMessageById(msg.id);
                  window.location.reload();
                },
              },
              // { icon: <BiDislike />, onClick: () => {} },
              {
                icon: copied ? (
                  <FaCheck className={classes.checkMark} />
                ) : (
                  <LuCopy />
                ),
                onClick: () => handleCopy(msg.content),
              },
            ].map((action, index) => (
              <button
                key={index}
                className={classes.actionButton}
                onClick={action.onClick}
              >
                {action.icon}
              </button>
            ))}
          </div>
        </>
      )}

      {userData && (
        <ManageAccountLayout
          isActive={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          isLoggedIn={true}
          userData={userData}
        />
      )}
    </div>
  );
});

// CodeBlock component to handle individual code block rendering
const CodeBlock = ({ code, language, handleDownload }) => {
  const codeRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // Handle copying to clipboard (preserving formatting)
  const handleCopy = (code) => {
    copyToClipboard(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };
  const blackBackgroundStyle = {
    'pre[class*="language-"]': {
      backgroundColor: "#000000", // Pure black background
      color: "#ffffff", // White text
      padding: "1rem",
      borderRadius: "0.5rem",
      overflow: "auto",
    },
    'code[class*="language-"]': {
      color: "#ffffff", // White text
    },
  };
  return (
    <div className={classes.codeBlockWrapper}>
      <div className={classes.codeHeader}>
        <Text sm primitive50 className={classes.language}>
          {language}
        </Text>
        <div className={classes.copyAndDownload}>
          <button
            className={classes.copyButton}
            onClick={() => handleCopy(code)} // Pass code directly instead of ref
          >
            {copied ? <FaCheck /> : <LuCopy />}
          </button>
          <button
            className={classes.downloadButton}
            onClick={() => handleDownload(code, language)}
          >
            <HiOutlineDownload />
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        className={classes.customSyntaxHighlighter}
        language={language}
        PreTag="div"
        style={{
          ...atomDark, // Use atomOneDark for better token separation
          'code[class*="language-"]': {
            color: "#E6E6E6", // Adjust default text color
          },
          backgroundColor: "var(--Primitive-900)",
          "span.token.keyword": { color: "#A9E246" },
          "span.token.function": { color: "#A6E22E" },
          "span.token.variable": { color: "#FD971F" },
          "span.token.string": { color: "#E6DB74" },
          "span.token.operator": { color: "#FA417B" },
          "span.token.comment": { color: "#75715E", fontStyle: "italic" },
        }}
        ref={codeRef} // Attach ref to the SyntaxHighlighter
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

export default ResponseMessage;
